import React from 'react';
import { ArrowRight } from 'lucide-react';

/**
 * The Property Match Score's level, reasons and the agent's next step, the
 * same on Find Buyers, Prospects Buyer Match and Match properties (backend
 * match_score; rules approved on DOMA 13382461862).
 *
 * Strong needs a named or nearby suburb and nothing left to verify. Broad
 * discovery is a buyer who named only a capital city or no area: the property
 * fits their requirements, not an area they chose.
 */

// The score's parts and their ceilings, for tooltips.
export const SCORE_PARTS = [
  ['location', 'Location', 35], ['budget', 'Budget', 20], ['beds', 'Beds', 12],
  ['type', 'Type', 10], ['baths', 'Baths', 6], ['cars', 'Parking', 5], ['behaviour', 'Activity', 12],
];

export const scoreTitle = (parts) => (parts
  ? SCORE_PARTS.map(([key, label, max]) => `${label} ${parts[key] ?? 0}/${max}`).join(' · ')
  : undefined);

const LEVEL_CLS = {
  strong: 'bg-emerald-100 text-emerald-800',
  possible: 'bg-sky-100 text-sky-800',
  broad: 'bg-gray-100 text-gray-600',
  low: 'bg-white text-gray-600 ring-1 ring-gray-200',
};

// A reason the backend flags for checking ("Price: needs verification").
const isVerifyReason = (reason) => reason.endsWith(': needs verification');

/** The reasons behind a score. Data to verify is amber; a capital-city-only
 *  buyer's "Anywhere in ..." is grey. */
export const ReasonChips = ({ match }) => (match?.reasons || []).map((r, i) => (
  <span key={`${i}-${r}`} className={`rounded-full text-[11px] font-medium px-2 py-0.5 ${
    isVerifyReason(r) ? 'bg-amber-50 text-amber-700'
      : match.broad_location && r.startsWith('Anywhere in ') ? 'bg-gray-100 text-gray-600'
        : 'bg-[#55C5D0]/15 text-[#0e7490]'
  }`}>{r}</span>
));

/** The level, plus how much of the match rests on facts both sides gave
 *  (a 75 on half the information is not a 75 on all of it). */
export const MatchLevelChip = ({ match }) => (match?.level ? (
  <span className="inline-flex items-center gap-1.5">
    <span
      className={`rounded-full text-[11px] font-semibold px-2 py-0.5 ${LEVEL_CLS[match.level] || LEVEL_CLS.low}`}
      title={[scoreTitle(match.score_parts), match.score_version].filter(Boolean).join(' · ') || undefined}
    >
      {match.level_label}
    </span>
    {match.confidence != null && (
      <span className="text-[11px] text-gray-500" title="How much of this match rests on details both the buyer and the property have on file">
        {match.confidence}% known
      </span>
    )}
  </span>
) : null);

export const NextAction = ({ match, className = '' }) => (match?.next_action ? (
  <div className={`flex items-start gap-1 text-xs text-gray-600 ${className}`}>
    <ArrowRight className="w-3.5 h-3.5 mt-px shrink-0 text-[#3a99a3]" />
    <span>{match.next_action}</span>
  </div>
) : null);
